
import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, Zap, TrendingUp, Clock, AlertTriangle } from 'lucide-react';

const Calculator: React.FC = () => {
  const [step, setStep] = useState(1);
  const [service, setService] = useState('');
  const [height, setHeight] = useState(25);
  const [access, setAccess] = useState('');
  const [showResults, setShowResults] = useState(false);

  const pricingMatrix: any = {
    removal: { base: 600, perFoot: 25, label: 'Tree Removal' },
    trimming: { base: 350, perFoot: 15, label: 'Tree Trimming' },
    stump: { base: 250, perFoot: 8, label: 'Stump Grinding' },
    emergency: { base: 1200, perFoot: 35, label: 'Emergency Work' }
  };

  const accessMultipliers: any = {
    easy: 1,
    moderate: 1.3,
    difficult: 1.6,
    hazard: 2
  };

  const calculatePrice = () => {
    if (!service || !access) return { lowEnd: 0, highEnd: 0 };
    const pricing = pricingMatrix[service];
    const multiplier = accessMultipliers[access];
    const basePrice = pricing.base + (pricing.perFoot * height);
    const finalPrice = basePrice * multiplier;
    return {
      lowEnd: Math.floor(finalPrice * 0.85),
      highEnd: Math.ceil(finalPrice * 1.15)
    };
  };

  const { lowEnd, highEnd } = calculatePrice();
  const estimatedTime = Math.ceil(height / 10) + (access === 'easy' ? 0 : access === 'moderate' ? 2 : 4);

  const handleServiceSelect = (val: string) => {
    setService(val);
    setTimeout(() => setStep(2), 300);
  };

  const handleAccessSelect = (val: string) => {
    setAccess(val);
    setShowResults(true);
  };

  return (
    <section id="calculator" className="py-24 bg-[#0F1419] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase italic text-white tracking-tighter">AI Quote & Revenue Impact</h2>
          <p className="text-xl text-[#1A4D2E] font-bold uppercase tracking-widest">See how automation turns cold leads into high-ticket jobs.</p>
        </div>

        <div className="bg-[#F4E4C1] rounded-[2rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row min-h-[800px] border-4 border-[#4E3629]/20">
          {/* Left Side: The Quote Tool */}
          <div className="lg:w-1/2 bg-white/50 p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-[#4E3629]/10 text-[#0F1419]">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 bg-[#1A4D2E] rounded-xl flex items-center justify-center text-white">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-black uppercase italic leading-none">Instant Quote Tool</h3>
                <p className="text-xs font-bold text-[#4E3629] opacity-60 uppercase tracking-widest mt-1">Simulate customer experience</p>
              </div>
            </div>

            {/* Step Indicators */}
            <div className="flex justify-between mb-16 relative">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex flex-col items-center gap-2 z-10">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black transition-all duration-500 border-4 ${
                    step >= s ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-gray-400 border-gray-200'
                  } ${step === s ? 'ring-8 ring-[#1A4D2E]/10 scale-110' : ''}`}>
                    {step > s ? <Check className="w-7 h-7" /> : s}
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${step >= s ? 'text-[#1A4D2E]' : 'text-gray-400'}`}>
                    {s === 1 ? 'Service' : s === 2 ? 'Height' : 'Access'}
                  </span>
                </div>
              ))}
              <div className="absolute top-6 left-0 w-full h-[4px] bg-gray-200 -z-0" />
            </div>

            {/* Step Content */}
            <div className="space-y-8">
              {step === 1 && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <label className="block text-xl font-black uppercase mb-8 italic">Choose Service Type</label>
                  <div className="grid grid-cols-2 gap-6">
                    {Object.keys(pricingMatrix).map((key) => (
                      <button
                        key={key}
                        onClick={() => handleServiceSelect(key)}
                        className={`p-8 rounded-2xl border-4 text-left transition-all hover:scale-[1.02] ${
                          service === key ? 'border-[#1A4D2E] bg-white shadow-xl' : 'border-[#4E3629]/10 bg-white/30 hover:border-[#1A4D2E]'
                        }`}
                      >
                        <span className="block text-4xl mb-3">
                          {key === 'removal' ? '🪓' : key === 'trimming' ? '✂️' : key === 'stump' ? '🌳' : '⚡'}
                        </span>
                        <span className="block font-black uppercase italic">{pricingMatrix[key].label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <label className="block text-xl font-black uppercase mb-8 italic">Specify Height</label>
                  <div className="bg-white p-10 rounded-2xl shadow-xl border-4 border-[#1A4D2E]/10">
                    <div className="flex justify-between items-end mb-10">
                      <span className="text-7xl font-black text-[#1A4D2E]">{height}ft</span>
                      <span className="text-[#4E3629] font-black mb-4 uppercase text-xs tracking-widest opacity-50">Slide to adjust</span>
                    </div>
                    <input 
                      type="range" min="10" max="100" step="5" value={height}
                      onChange={(e) => setHeight(parseInt(e.target.value))}
                      className="w-full h-4 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1A4D2E]"
                    />
                  </div>
                  <button 
                    onClick={() => setStep(3)}
                    className="w-full mt-10 bg-[#FF6B35] text-white font-black py-8 rounded-2xl hover:bg-[#e05a2b] transition-all flex items-center justify-center gap-3 uppercase text-xl shadow-xl shadow-orange-500/20"
                  >
                    CONTINUE <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              )}

              {step === 3 && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <label className="block text-xl font-black uppercase mb-8 italic">Access Status</label>
                  <div className="grid grid-cols-2 gap-6">
                    {['easy', 'moderate', 'difficult', 'hazard'].map((key) => (
                      <button
                        key={key}
                        onClick={() => handleAccessSelect(key)}
                        className={`p-6 rounded-2xl border-4 text-left transition-all ${
                          access === key ? 'border-[#1A4D2E] bg-white shadow-xl' : 'border-[#4E3629]/10 bg-white/30 hover:border-[#1A4D2E]'
                        }`}
                      >
                        <span className="block text-3xl mb-2">{key === 'easy' ? '✅' : key === 'moderate' ? '⚠️' : key === 'difficult' ? '🚧' : '⛔'}</span>
                        <span className="block font-black uppercase italic">{key}</span>
                        <span className="text-[10px] text-[#4E3629] font-bold leading-tight uppercase opacity-60">
                          {key === 'easy' ? 'Open yard/driveway' : key === 'moderate' ? 'Backyard/fences' : key === 'difficult' ? 'Power lines/buildings' : 'Severe hazards'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showResults && (
                <div className="mt-12 p-10 bg-[#1A4D2E] rounded-3xl text-white animate-in zoom-in duration-500 shadow-2xl relative overflow-hidden border-4 border-[#FF6B35]">
                  <div className="absolute top-0 right-0 p-6 opacity-10">
                    <Zap className="w-24 h-24" />
                  </div>
                  <div className="relative z-10 text-center">
                    <p className="text-[#FF6B35] font-black uppercase tracking-[0.3em] text-[10px] mb-4">Estimated Project Range</p>
                    <p className="text-6xl font-black mb-10 italic tracking-tighter">${lowEnd.toLocaleString()} - ${highEnd.toLocaleString()}</p>
                    
                    <div className="grid grid-cols-2 gap-6 text-left mb-8">
                      <div className="bg-white/10 p-5 rounded-xl border border-white/10">
                        <p className="text-[9px] text-[#F4E4C1] font-black uppercase tracking-widest mb-1">Crew Duration</p>
                        <p className="font-black text-lg">{estimatedTime}-{estimatedTime + 2} Hours</p>
                      </div>
                      <div className="bg-white/10 p-5 rounded-xl border border-white/10">
                        <p className="text-[9px] text-[#F4E4C1] font-black uppercase tracking-widest mb-1">Risk Factor</p>
                        <p className="font-black text-lg text-[#FF6B35] uppercase italic">{access === 'hazard' ? 'Critical' : 'Standard'}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => {setStep(1); setAccess(''); setShowResults(false);}}
                      className="text-xs font-black uppercase tracking-widest underline opacity-40 hover:opacity-100 transition-opacity"
                    >
                      Start new quote
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side: Follow-up & Revenue */}
          <div className="lg:w-1/2 p-8 md:p-12 bg-white text-[#0F1419] overflow-y-auto max-h-[800px]">
            <div className="flex items-center gap-4 mb-12">
              <Zap className="w-10 h-10 text-[#FF6B35]" />
              <h3 className="text-3xl font-black uppercase italic leading-none tracking-tighter">The "Money Machine"</h3>
            </div>

            <div className="relative space-y-10 pl-10 border-l-4 border-dashed border-[#4E3629]/10 mb-16">
              <TimelineItem 
                time="INSTANT (30s)" 
                type="EMAIL" 
                msg={`Subject: Your $${lowEnd || '1,600'}-$${highEnd || '2,200'} tree quote is ready. Book my calendar directly to lock this in.`}
              />
              <TimelineItem 
                time="INSTANT (2m)" 
                type="SMS" 
                msg={`Hey! Saw your quote request for the ${height}ft job. Looking to handle this this week or next? - Arbor Advantage AI`}
              />
              <TimelineItem 
                time="5 MINS" 
                type="AI CALL" 
                msg={`"Hi, this is your automated assistant. I see you just got a quote. Any questions about the ${access} access requirements?"`}
                urgent
              />
              <TimelineItem 
                time="DAY 2" 
                type="SMS" 
                msg={`Morning! We just had a slot open up this Thursday for a ${service || 'tree'} crew. Want me to hold it for you?`}
              />
              <TimelineItem 
                time="DAY 5" 
                type="EMAIL" 
                msg={`Subject: $200 OFF Voucher Inside. I'm filling the schedule for next week and want to prioritize your ${height}ft tree removal.`}
                warning
              />
            </div>

            <div className="bg-[#0F1419] rounded-[2.5rem] p-10 text-white shadow-2xl border-4 border-[#1A4D2E]">
              <h4 className="flex items-center gap-3 text-[#1A4D2E] font-black uppercase tracking-[0.2em] text-xs mb-8">
                <TrendingUp className="w-5 h-5 text-[#FF6B35]" /> ANNUAL REVENUE IMPACT
              </h4>
              
              <div className="space-y-8">
                <div className="flex justify-between items-end border-b border-white/5 pb-6">
                  <div>
                    <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">MANUAL PROCESS</p>
                    <p className="text-2xl font-black opacity-30 italic line-through">$240,000</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-[#1A4D2E] font-black uppercase tracking-widest mb-1">OUR SYSTEM</p>
                    <p className="text-5xl font-black text-[#FF6B35] italic tracking-tighter">$556,800</p>
                  </div>
                </div>

                <div className="bg-[#1A4D2E]/20 border-2 border-[#1A4D2E]/40 p-8 rounded-2xl text-center relative overflow-hidden group">
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <p className="text-xs font-black text-gray-400 mb-2 uppercase tracking-widest">ADDITIONAL CASH FLOW</p>
                  <p className="text-5xl font-black text-[#1A4D2E] tracking-tighter">+$316,800</p>
                  <div className="mt-6 inline-flex items-center justify-center gap-3 text-[10px] font-black text-white/40 uppercase tracking-widest bg-white/5 px-4 py-2 rounded-full">
                    <Check className="w-4 h-4 text-[#1A4D2E]" /> PAYS FOR ITSELF IN 3 DAYS
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TimelineItem = ({ time, type, msg, urgent = false, warning = false }: any) => (
  <div className={`relative animate-in slide-in-from-left-4 duration-700`}>
    <div className={`absolute -left-[54px] top-1 w-6 h-6 rounded-full border-4 border-[#F4E4C1] ${
      urgent ? 'bg-[#FF6B35] animate-pulse' : warning ? 'bg-[#FFA500]' : 'bg-[#1A4D2E]'
    }`} />
    <div className="flex justify-between items-center mb-2">
      <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">{time}</span>
      <span className={`text-[10px] font-black px-3 py-1 rounded-full border ${
        type === 'EMAIL' ? 'border-blue-200 text-blue-600 bg-blue-50' : 
        type === 'SMS' ? 'border-green-200 text-green-600 bg-green-50' : 
        'border-purple-200 text-purple-600 bg-purple-50'
      }`}>{type}</span>
    </div>
    <div className={`p-5 rounded-2xl border-2 text-sm font-bold leading-relaxed ${
      urgent ? 'bg-red-50 border-red-200 text-red-900' : warning ? 'bg-orange-50 border-orange-200 text-orange-900' : 'bg-gray-50 border-gray-100 text-gray-700'
    }`}>
      {msg}
    </div>
  </div>
);

export default Calculator;
